import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Badge as MuiBadge,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Stack,
  Tooltip,
  Card,
  CardContent,
  alpha,
  useTheme,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import {
  CheckCircle,
  Pending,
  Engineering,
  Science,
  AttachMoney,
  Business,
  Assignment,
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  Search,
} from "@mui/icons-material";
import { format, isValid, addMonths, subMonths } from "date-fns";
import axios from "axios";
import { DATA_SAVE_URL } from "../utils/UrlHandle";

// Helper function to safely format dates
const safeFormatDate = (date, formatString = "PPpp") => {
  if (!date) return "Not started";
  try {
    const parsedDate = new Date(date);
    if (!isValid(parsedDate)) return "Invalid date";
    return format(parsedDate, formatString);
  } catch (error) {
    return "Invalid date";
  }
};

// Helper function to get status color
const getStatusColor = (status) => ({
  bg: status ? "#4caf50" : "#ff9800",
  text: status ? "Completed" : "Pending",
});

// Status badge component
const StatusBadge = ({ status, label, timestamp }) => {
  const { bg, text } = getStatusColor(status);
  return (
    <Tooltip title={safeFormatDate(timestamp, "PPpp")} arrow>
      <Chip
        size="small"
        label={`${label}: ${text}`}
        sx={{
          bgcolor: alpha(bg, 0.15),
          color: bg,
          fontWeight: 500,
          "& .MuiChip-label": { px: 1 },
          border: `1px solid ${alpha(bg, 0.3)}`,
        }}
        icon={
          status ? (
            <CheckCircle sx={{ fontSize: 14 }} />
          ) : (
            <Pending sx={{ fontSize: 14 }} />
          )
        }
      />
    </Tooltip>
  );
};

// Custom calendar day component with badges showing total job count
const DayWithBadge = React.memo((props) => {
  const {
    day,
    outsideCurrentMonth,
    selected,
    jobsCountMap = new Map(),
    ...other
  } = props;

  const dateKey = format(day, "yyyy-MM-dd");
  const jobCount = jobsCountMap.get(dateKey) || 0;

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <PickersDay
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={selected}
        sx={{
          fontSize: "1rem",
          fontWeight: 500,
          width: 48,
          height: 48,
          "@media (min-width: 600px)": {
            width: 56,
            height: 56,
          },
          "&.Mui-selected": {
            backgroundColor: "#4caf50 !important",
            color: "white !important",
            "&:hover": {
              backgroundColor: "#45a049 !important",
            },
          },
        }}
        {...other}
      />
      {jobCount > 0 && !outsideCurrentMonth && (
        <MuiBadge
          badgeContent={jobCount}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 11,
              height: 20,
              minWidth: 20,
              borderRadius: 10,
              fontWeight: "bold",
              backgroundColor: "#2196f3",
              color: "white",
              border: "2px solid white",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              transform: "scale(1) translate(30%, -30%)",
            },
          }}
        />
      )}
    </Box>
  );
});

// Colorful Stat Card Component
const StatCard = ({ icon, title, value, color, gradient }) => (
  <Card
    sx={{
      background: gradient,
      color: "white",
      borderRadius: 3,
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
      },
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: alpha(color, 0.3),
            borderRadius: 2,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// Main Calendar Component
const LabRequestCalendar = () => {
  const theme = useTheme();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [labRequests, setLabRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  // Year and Month selection
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedMonth, setSelectedMonth] = useState(4); // May is 4 (0-indexed)

  // Generate years from 2025 to current year + 2
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear + 2 - 2024 },
    (_, i) => 2025 + i,
  );

  const months = [
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  // Function to fetch data from API
  const fetchLabRequests = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${DATA_SAVE_URL}/api/v1/institute/all/jobsByMonth`,
        {
          params: {
            year: selectedYear,
            month: selectedMonth + 1,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      let requestsData = response.data;

      // Handle different response structures
      if (!Array.isArray(requestsData)) {
        if (
          requestsData.allRequests &&
          Array.isArray(requestsData.allRequests)
        ) {
          requestsData = requestsData.allRequests;
        } else if (requestsData.data && Array.isArray(requestsData.data)) {
          requestsData = requestsData.data;
        } else if (
          requestsData.content &&
          Array.isArray(requestsData.content)
        ) {
          requestsData = requestsData.content;
        } else {
          console.error("Unexpected API response structure:", requestsData);
          requestsData = [];
        }
      }

      console.log(
        "Fetched requests for",
        months[selectedMonth].label,
        selectedYear,
        ":",
        requestsData.length,
      );
      setLabRequests(requestsData);

      // Force calendar to refresh with new data
      setCalendarRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error("Error fetching lab requests:", err);
      setError("Failed to load data. Please try again.");
      setLabRequests([]);
      setCalendarRefreshKey((prev) => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  // Load data when year/month changes
  useEffect(() => {
    fetchLabRequests();
  }, [selectedYear, selectedMonth]);

  // Update calendar view when year/month dropdown changes
  useEffect(() => {
    const newDate = new Date(selectedYear, selectedMonth, 1);
    setCurrentMonth(newDate);
  }, [selectedYear, selectedMonth]);

  // Extract assignUser from labRequestTests or request level
  const enrichRequestsWithAssignUser = (requests) => {
    if (!Array.isArray(requests)) return [];
    return requests.map((request) => ({
      ...request,
      assignUser:
        request.assignUser || request.labRequestTests?.[0]?.assignUser || null,
    }));
  };

  const enrichedLabRequests = useMemo(
    () => enrichRequestsWithAssignUser(labRequests),
    [labRequests],
  );

  // Group requests by date and count total jobs per day
  const jobsCountByDate = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(enrichedLabRequests)) return map;

    enrichedLabRequests.forEach((request) => {
      if (request.regDate) {
        const dateKey = request.regDate;
        const currentCount = map.get(dateKey) || 0;
        map.set(dateKey, currentCount + 1);
      }
    });

    console.log("Jobs count by date:", Array.from(map.entries()));
    return map;
  }, [enrichedLabRequests, calendarRefreshKey]);

  // Get all requests grouped by date for the overview section
  const requestsByDate = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(enrichedLabRequests)) return map;

    enrichedLabRequests.forEach((request) => {
      if (request.regDate) {
        const dateKey = request.regDate;
        if (!map.has(dateKey)) {
          map.set(dateKey, []);
        }
        map.get(dateKey).push(request);
      }
    });
    return map;
  }, [enrichedLabRequests]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!Array.isArray(enrichedLabRequests)) {
      return {
        totalRequests: 0,
        totalValue: 0,
        completedRequests: 0,
        pendingRequests: 0,
      };
    }
    const totalRequests = enrichedLabRequests.length;
    const totalValue = enrichedLabRequests.reduce(
      (sum, r) => sum + (r.grandTotal || 0),
      0,
    );
    const completedRequests = enrichedLabRequests.filter(
      (r) => r.complete === true,
    ).length;
    const pendingRequests = totalRequests - completedRequests;
    return { totalRequests, totalValue, completedRequests, pendingRequests };
  }, [enrichedLabRequests]);

  // Handle date click - changes background color to green
  const handleDateClick = (date) => {
    setSelectedDate(date);
    const dateKey = format(date, "yyyy-MM-dd");
    const requests = requestsByDate.get(dateKey) || [];
    if (requests.length > 0) {
      setSelectedRequests(requests);
      setDialogOpen(true);
    }
  };

  // Navigation handlers for calendar arrows
  const handlePrevMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    setSelectedYear(newMonth.getFullYear());
    setSelectedMonth(newMonth.getMonth());
  };

  const handleNextMonth = () => {
    const newMonth = addMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    setSelectedYear(newMonth.getFullYear());
    setSelectedMonth(newMonth.getMonth());
  };

  // Handle year dropdown change
  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  // Handle month dropdown change
  const handleMonthChange = (month) => {
    setSelectedMonth(month);
  };

  // Handle manual load
  const handleLoadData = () => {
    fetchLabRequests();
  };

  // Handle click on calendar overview card
  const handleOverviewCardClick = (date) => {
    setSelectedDate(date);
    const dateKey = format(date, "yyyy-MM-dd");
    const requests = requestsByDate.get(dateKey) || [];
    if (requests.length > 0) {
      setSelectedRequests(requests);
      setDialogOpen(true);
    }
    // Force calendar to refresh
    setCalendarRefreshKey((prev) => prev + 1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: 1600, mx: "auto", p: 3 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
            color: "white",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <CalendarToday sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Lab Request Calendar
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Track and manage all laboratory test requests
                </Typography>
              </Box>
            </Stack>

            {/* Year and Month Selection */}
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl
                size="small"
                sx={{
                  minWidth: 120,
                  bgcolor: "rgba(255,255,255,0.1)",
                  borderRadius: 1,
                }}
              >
                <InputLabel sx={{ color: "white" }}>Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                  label="Year"
                  sx={{
                    color: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  minWidth: 140,
                  bgcolor: "rgba(255,255,255,0.1)",
                  borderRadius: 1,
                }}
              >
                <InputLabel sx={{ color: "white" }}>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => handleMonthChange(e.target.value)}
                  label="Month"
                  sx={{
                    color: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleLoadData}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Search />
                  )
                }
                sx={{
                  bgcolor: "white",
                  color: "#667eea",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.9)",
                  },
                }}
              >
                Load Data
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading Indicator */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<Assignment sx={{ fontSize: 32, color: "white" }} />}
                  title="Total Requests"
                  value={stats.totalRequests}
                  color="#2196f3"
                  gradient="linear-gradient(135deg, #2196f3 0%, #1976d2 100%)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<AttachMoney sx={{ fontSize: 32, color: "white" }} />}
                  title="Total Value"
                  value={`LKR ${stats.totalValue.toLocaleString()}`}
                  color="#4caf50"
                  gradient="linear-gradient(135deg, #4caf50 0%, #388e3c 100%)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<CheckCircle sx={{ fontSize: 32, color: "white" }} />}
                  title="Completed"
                  value={stats.completedRequests}
                  color="#ff9800"
                  gradient="linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<Pending sx={{ fontSize: 32, color: "white" }} />}
                  title="Pending"
                  value={stats.pendingRequests}
                  color="#f44336"
                  gradient="linear-gradient(135deg, #f44336 0%, #d32f2f 100%)"
                />
              </Grid>
            </Grid>

            <Grid container spacing={4}>
              {/* Calendar Section */}
              <Grid item xs={12} lg={7}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
                    p: 2,
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2, px: 2 }}
                  >
                    <IconButton onClick={handlePrevMonth}>
                      <ChevronLeft />
                    </IconButton>
                    <Typography variant="h6" fontWeight="bold">
                      {format(currentMonth, "MMMM yyyy")}
                    </Typography>
                    <IconButton onClick={handleNextMonth}>
                      <ChevronRight />
                    </IconButton>
                  </Stack>

                  <Box
                    sx={{
                      "& .MuiDateCalendar-root": {
                        width: "100%",
                        maxWidth: "100%",
                        height: "auto",
                      },
                      "& .MuiPickersCalendarHeader-root": {
                        display: "none",
                      },
                      "& .MuiDayCalendar-weekDayLabel": {
                        fontSize: "1rem",
                        fontWeight: 600,
                        width: 56,
                        margin: "8px 0",
                        color: theme.palette.text.secondary,
                      },
                      "& .MuiPickersSlideTransition-root": {
                        minHeight: 420,
                      },
                    }}
                  >
                    <DateCalendar
                      key={calendarRefreshKey}
                      value={selectedDate}
                      onChange={(newDate) =>
                        newDate && handleDateClick(newDate)
                      }
                      showDaysOutsideCurrentMonth={true}
                      sx={{
                        width: "100%",
                        "& .MuiPickersDay-root": {
                          fontSize: "1rem",
                          fontWeight: 500,
                          width: 48,
                          height: 48,
                          margin: "4px",
                          "@media (min-width: 600px)": {
                            width: 56,
                            height: 56,
                            margin: "6px",
                          },
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "scale(1.05)",
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1,
                            ),
                          },
                        },
                      }}
                      slots={{
                        day: (props) => (
                          <DayWithBadge
                            {...props}
                            jobsCountMap={jobsCountByDate}
                          />
                        ),
                      }}
                    />
                  </Box>

                  {/* Legend */}
                  <Box
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={3}
                      justifyContent="center"
                      flexWrap="wrap"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 1,
                            bgcolor: "#2196f3",
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Total Jobs per Day (Blue Badge)
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 1,
                            bgcolor: "#4caf50",
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Selected Date (Green Background)
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Paper>
              </Grid>

              {/* Stats Section with Scrollbar */}
              <Grid item xs={12} lg={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Calendar Overview
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {requestsByDate.size} date
                    {requestsByDate.size !== 1 ? "s" : ""} with lab requests
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      maxHeight: 400,
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: alpha(theme.palette.primary.main, 0.5),
                        borderRadius: "4px",
                        "&:hover": {
                          background: alpha(theme.palette.primary.main, 0.7),
                        },
                      },
                    }}
                  >
                    {Array.from(requestsByDate.entries()).length === 0 ? (
                      <Typography
                        color="text.secondary"
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        No lab requests found for{" "}
                        {months.find((m) => m.value === selectedMonth)?.label}{" "}
                        {selectedYear}
                      </Typography>
                    ) : (
                      <Stack spacing={2}>
                        {Array.from(requestsByDate.entries())
                          .sort((a, b) => new Date(a[0]) - new Date(b[0]))
                          .map(([date, requests], index) => {
                            const completedCount = requests.filter(
                              (r) => r.complete === true,
                            ).length;
                            const pendingCount = requests.filter(
                              (r) => r.complete === false,
                            ).length;

                            // Determine card color based on pending status
                            // Red if there are pending requests, Green if all are completed
                            const cardGradient =
                              pendingCount > 0
                                ? "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)" // Red gradient for pending
                                : "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)"; // Green gradient for completed

                            return (
                              <Card
                                key={date}
                                sx={{
                                  cursor: "pointer",
                                  transition: "all 0.3s",
                                  background: cardGradient,
                                  color: "white",
                                  "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                                  },
                                }}
                                onClick={() =>
                                  handleOverviewCardClick(new Date(date))
                                }
                              >
                                <CardContent>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ opacity: 0.9 }}
                                      >
                                        {safeFormatDate(date, "EEEE")}
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                      >
                                        {safeFormatDate(date, "MMMM d, yyyy")}
                                      </Typography>
                                    </Box>
                                    <MuiBadge
                                      badgeContent={requests.length}
                                      sx={{
                                        "& .MuiBadge-badge": {
                                          fontSize: 14,
                                          fontWeight: "bold",
                                          backgroundColor: "#ffffff",
                                          color:
                                            pendingCount > 0
                                              ? "#f44336"
                                              : "#4caf50",
                                          boxShadow:
                                            "0 2px 4px rgba(0,0,0,0.2)",
                                        },
                                      }}
                                    />
                                  </Stack>

                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{ mt: 1 }}
                                  >
                                    <Chip
                                      size="small"
                                      label={`Complete: ${completedCount}`}
                                      sx={{
                                        bgcolor: alpha("#ffffff", 0.3),
                                        color: "white",
                                        fontWeight: 500,
                                        fontSize: "0.7rem",
                                      }}
                                    />
                                    <Chip
                                      size="small"
                                      label={`Pending: ${pendingCount}`}
                                      sx={{
                                        bgcolor: alpha("#ffffff", 0.3),
                                        color: "white",
                                        fontWeight: 500,
                                        fontSize: "0.7rem",
                                      }}
                                    />
                                  </Stack>

                                  <Typography
                                    variant="body2"
                                    sx={{ mt: 1, opacity: 0.9 }}
                                  >
                                    Total Value: LKR{" "}
                                    {requests
                                      .reduce(
                                        (sum, r) => sum + (r.grandTotal || 0),
                                        0,
                                      )
                                      .toLocaleString()}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      mt: 0.5,
                                      display: "block",
                                      opacity: 0.8,
                                    }}
                                  >
                                    {requests.reduce(
                                      (sum, r) =>
                                        sum + (r.labRequestTests?.length || 0),
                                      0,
                                    )}{" "}
                                    test request
                                    {requests.reduce(
                                      (sum, r) =>
                                        sum + (r.labRequestTests?.length || 0),
                                      0,
                                    ) !== 1
                                      ? "s"
                                      : ""}
                                  </Typography>
                                </CardContent>
                              </Card>
                            );
                          })}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {/* Detailed Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              maxHeight: "85vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5" fontWeight="bold">
                Lab Requests
                {selectedRequests.length > 0 && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ ml: 2, opacity: 0.9 }}
                  >
                    {safeFormatDate(
                      selectedRequests[0]?.regDate,
                      "MMMM d, yyyy",
                    )}
                  </Typography>
                )}
              </Typography>
              <Chip
                label={`${selectedRequests.length} request${selectedRequests.length !== 1 ? "s" : ""}`}
                sx={{ bgcolor: "white", color: "#667eea", fontWeight: "bold" }}
                size="small"
              />
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 2 }}>
            {selectedRequests.map((request, index) => {
              const isPending = !request.complete;

              return (
                <Paper
                  key={request.id}
                  elevation={0}
                  sx={{
                    mb: index !== selectedRequests.length - 1 ? 3 : 0,
                    p: 3,
                    borderRadius: 3,
                    border: `2px solid ${isPending ? alpha("#f44336", 0.5) : alpha("#4caf50", 0.3)}`,
                    transition: "all 0.2s",
                    background: isPending
                      ? "linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)"
                      : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                    boxShadow: isPending
                      ? `0 4px 12px ${alpha("#f44336", 0.2)}`
                      : "none",
                    "&:hover": {
                      boxShadow: theme.shadows[2],
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {/* Request Header */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Assignment fontSize="small" color="primary" />
                        <Typography variant="h4" color="text.secondary">
                          Job No :
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight="medium"
                          color="#2196f3"
                        >
                          JOB/{request.year + "/" + request.autoIdByYear}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ mt: 1, mb: 0.5 }}
                      >
                        {request.project}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={request.complete ? "Complete" : "In Progress"}
                        size="small"
                        sx={{
                          bgcolor: request.complete
                            ? alpha("#4caf50", 0.15)
                            : alpha("#f44336", 0.15),
                          color: request.complete ? "#4caf50" : "#f44336",
                          fontWeight: "bold",
                          border: request.complete
                            ? "none"
                            : `1px solid ${alpha("#f44336", 0.3)}`,
                        }}
                      />
                      <Chip
                        label={request.paid ? "Paid" : "Unpaid"}
                        size="small"
                        sx={{
                          bgcolor: request.paid
                            ? alpha("#2196f3", 0.15)
                            : alpha("#ff9800", 0.15),
                          color: request.paid ? "#2196f3" : "#ff9800",
                          fontWeight: 500,
                        }}
                      />
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  {/* Institute & Assigned User */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Business fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Institute:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {request.institute?.instituteName || "N/A"}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Engineering fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Assigned To:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {request.assignUser
                            ? `${request.assignUser.firstName} ${request.assignUser.lastName}`
                            : "Unassigned"}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Tests Section */}
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    <Science
                      sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
                    />
                    Tests ({request.labRequestTests?.length || 0})
                  </Typography>

                  <Stack spacing={2}>
                    {(request.labRequestTests || []).map((test) => {
                      // Check if labTest exists and has id 56
                      const shouldHideStatus = test.labTest?.id === 56;

                      return (
                        <Paper
                          key={test.id}
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(
                              theme.palette.background.default,
                              0.5,
                            ),
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={shouldHideStatus ? 12 : 6}>
                              <Typography variant="body1" fontWeight="bold">
                                {test.labTest?.testName || "Unknown Test"}
                              </Typography>
                              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Quantity: {test.quantity}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Amount: LKR {test.labTest?.amount || 0}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color="primary.main"
                                >
                                  Total: LKR {test.total}
                                </Typography>
                              </Stack>
                            </Grid>
                            {!shouldHideStatus && (
                              <Grid item xs={12} md={6}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  <StatusBadge
                                    status={test.finalized}
                                    label="Finalized"
                                    timestamp={test.finalizedDateTime}
                                  />
                                  <StatusBadge
                                    status={test.checked}
                                    label="Checked"
                                    timestamp={test.checkedDateTime}
                                  />
                                  <StatusBadge
                                    status={test.checkedByEngineer}
                                    label="Engineer Check"
                                    timestamp={test.checkedByEngineerDateTime}
                                  />
                                </Stack>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      );
                    })}
                  </Stack>

                  {/* Total */}
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}
                  >
                    <AttachMoney fontSize="small" color="primary" />
                    <Typography variant="h4" color="text.secondary">
                      Grand Total:
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="green">
                      LKR {(request.grandTotal || 0).toLocaleString()}
                    </Typography>
                  </Stack>

                  {/* Pending Job Indicator */}
                  {isPending && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 1,
                        display: "flex",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Chip
                        icon={<Pending sx={{ fontSize: 16 }} />}
                        label="Pending Job - In Progress"
                        size="small"
                        sx={{
                          bgcolor: alpha("#f44336", 0.1),
                          color: "#f44336",
                          fontWeight: "bold",
                          border: `1px solid ${alpha("#f44336", 0.3)}`,
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              );
            })}
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default LabRequestCalendar;
